import React from 'react'
import { useFlexLayout, useRowSelect, useTable } from 'react-table'

import { noop } from 'lodash'

import {
  CheckboxWrapper,
  DesktopSpinner,
  LoadingWrapper,
  MobileSkeleton,
  StyledCell,
  StyledCheckBox,
  StyledRowGroup,
  StyledTable,
  StyledTableHead,
  StyledTableRow
} from './Table.styled'

import TableMenu from './TableMenu'
import TableRows from './TableRows'
import { withConfigTheme } from '@core/hocs'
import i18n from '@core/config/i18n'

const Table = (
  {
    columns,
    data,
    menus = [],
    menuType = 'link',
    loading,
    canSelectRow,
    configTheme,
    tableWidth = '750px', //  Min Table Width for setting mobile width
    tableHeight = '500px', // Max Table Height this props not affect mobile table height
    menuWidth = '100px',

    // Event
    onRowSelectChanged = noop,
    onRowClicked,
    onMenuClicked = noop,
    hideCheckboxValue,
    hideCheckboxKey,
    // Misc
    ...props
  },
  ref
) => {
  const {
    headerGroups,
    rows,
    prepareRow,
    state: { selectedRowIds }
  } = useTable({ columns, data }, useRowSelect, useFlexLayout, (hooks) => {
    hooks.allColumns.push((columns) => [
      // Let's make a column for selection
      ...(canSelectRow
        ? [
            {
              id: 'selection',
              width: '50px',
              // The header can use the table's getToggleAllRowsSelectedProps method
              // to render a checkbox
              Header: (e) => {
                const { getToggleAllRowsSelectedProps, toggleRowSelected } = e
                if (hideCheckboxKey && hideCheckboxValue) {
                  const keys = Object.keys(e.state.selectedRowIds)
                  return (
                    <CheckboxWrapper>
                      <StyledCheckBox
                        type="checkbox"
                        checked={keys.length !== 0}
                        indeterminate={
                          keys.length < rows.length && keys.length !== 0
                        }
                        onClick={(event) => {
                          rows.forEach((itemRows) => {
                            if (
                              itemRows.original[hideCheckboxKey] ===
                              hideCheckboxValue
                            ) {
                              toggleRowSelected(itemRows.id, false)
                            } else {
                              toggleRowSelected(
                                itemRows.id,
                                event.target.checked
                              )
                            }
                          })
                        }}
                      />
                    </CheckboxWrapper>
                  )
                } else {
                  return (
                    <CheckboxWrapper>
                      <StyledCheckBox
                        type="checkbox"
                        {...getToggleAllRowsSelectedProps()}
                      />
                    </CheckboxWrapper>
                  )
                }
              },
              // The cell can use the individual row's getToggleRowSelectedProps method
              // to the render a checkbox
              Cell: ({ row }) => {
                if (hideCheckboxKey && hideCheckboxValue) {
                  if (row.original[hideCheckboxKey] === hideCheckboxValue) {
                    return (
                      <CheckboxWrapper>
                        <StyledCheckBox
                          disabled={true}
                          type="checkbox"
                          {...row.getToggleRowSelectedProps()}
                        />
                      </CheckboxWrapper>
                    )
                  }
                }
                return (
                  <CheckboxWrapper>
                    <StyledCheckBox
                      type="checkbox"
                      {...row.getToggleRowSelectedProps()}
                    />
                  </CheckboxWrapper>
                )
              }
            }
          ]
        : []),
      ...columns,
      ...(menus.length > 0
        ? [
            {
              id: 'menu',
              width: menuWidth,
              alignment: 'center', // All column "Action" always need center
              Header: () => i18n.t('common.table.action'),
              Cell: ({ row }) => (
                <TableMenu {...{ menus, menuType, row, onMenuClicked }} />
              )
            }
          ]
        : [])
    ])
  })

  const isInitial = React.useRef(true)

  React.useEffect(() => {
    if (!isInitial.current) {
      onRowSelectChanged(Object.keys(selectedRowIds))
    }

    isInitial.current = false
  }, [onRowSelectChanged, selectedRowIds])

  const renderLoading = React.useMemo(() => {
    return (
      <LoadingWrapper>
        <DesktopSpinner tip="Loading..." />
        <MobileSkeleton minWidth={tableWidth} active />
      </LoadingWrapper>
    )
  }, [tableWidth])

  return (
    <StyledTable role="table" ref={ref} minWidth={tableWidth} {...props}>
      <StyledTableHead role="rowgroup" configTheme={configTheme}>
        {headerGroups.map((headerGroup, idx) => (
          <StyledTableRow key={`header-group-${idx}`} role="rowheader">
            {headerGroup.headers.map((column, headerIdx) => (
              <StyledCell
                key={`header-${headerIdx}`}
                role="columnheader"
                type="header"
                width={column.width}
                alignment={column.alignment}
                configTheme={configTheme}
              >
                {column.render('Header')}
              </StyledCell>
            ))}
          </StyledTableRow>
        ))}
      </StyledTableHead>
      <StyledRowGroup role="rowgroup" maxHeight={tableHeight}>
        {!loading ? (
          <TableRows
            rows={rows}
            prepareRow={prepareRow}
            onRowClicked={onRowClicked}
          />
        ) : (
          renderLoading
        )}
      </StyledRowGroup>
    </StyledTable>
  )
}

export default withConfigTheme(React.forwardRef(Table))
